import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { FiCheckCircle, FiFileText, FiTarget, FiAlertCircle } from 'react-icons/fi';

const StyledModal = styled(Modal)`
  .modal-content {
    background:
      radial-gradient(circle at 12% 0%, rgba(117, 180, 245, 0.16), transparent 30%),
      linear-gradient(160deg, #fcfeff, #f1f7fd);
    color: #202630;
    border: 1px solid rgba(117, 154, 198, 0.22);
    border-radius: 29px;
    overflow: hidden;
    box-shadow: 0 38px 90px rgba(10, 31, 58, 0.28), inset 0 1px 0 #fff;
  }
  .modal-header {
    border: none;
    padding: 2rem 2rem 0.8rem;
    position: relative;
    background: transparent;
    
    .btn-close {
      position: absolute;
      top: 1.5rem;
      right: 1.5rem;
      filter: none;
      opacity: 0.62;
      z-index: 10;
    }
  }
  .modal-body {
    padding: 1rem 2rem 2rem;
    max-height: 70vh;
    overflow-y: auto;
    
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #aeb9c5; border-radius: 10px; }
  }
  .modal-footer {
    border-top: 1px solid rgba(17, 19, 24, 0.08);
    padding: 1.15rem 2rem;
    background: rgba(255, 255, 255, 0.55);
  }

  .modal-body > p {
    color: #697582;
    font-size: 0.93rem;
    line-height: 1.72;
  }

  @media (max-width: 576px) {
    .modal-dialog { margin: 8px; }
    .modal-content { border-radius: 23px; }
    .modal-header { padding: 1.5rem 1.25rem 0.7rem; }
    .modal-body { padding: 0.8rem 1.25rem 1.5rem; }
    .modal-footer { padding: 1rem 1.25rem; }
    .modal-header .btn-close { top: 1.1rem; right: 1rem; }
  }
`;

const SectionBox = styled.div`
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(75, 114, 160, 0.12);
  border-radius: 19px;
  padding: 1.35rem;
  margin-bottom: 0.85rem;
  box-shadow: inset 0 1px 0 #fff;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #326fae;
    margin-bottom: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  p, li {
    font-size: 0.95rem;
    color: #5f6d7b;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    word-break: keep-all;
  }
  
  strong {
    color: #253242;
    font-weight: 700;
  }

  ol {
    padding-left: 1.25rem;
    li {
        margin-bottom: 0.75rem;
        &::marker {
            color: #397bcf;
            font-weight: 800;
        }
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    padding: 0.4rem 0.8rem;
    background: #e8f3fd;
    border: 1px solid rgba(57, 123, 207, 0.16);
    border-radius: 999px;
    color: #397bcf;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 900;
    color: #171b22;
    margin: 0;
  }
`;

const TermsModal = ({ show, onHide }) => {
  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <HeaderTitle>
            <div className="badge"><FiFileText /> Service Terms</div>
            <h2>서비스 이용약관</h2>
        </HeaderTitle>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-5 text-slate-400 font-medium leading-relaxed">
            나니아랩 서비스를 이용해주셔서 감사합니다. <br />
            본 약관은 원활한 교육 서비스 이용을 위해 회사와 회원 간의 권리와 의무 사항을 담고 있습니다.
        </p>

        <SectionBox>
            <h3><FiTarget /> 제1조 (목적 및 정의)</h3>
            <p>본 약관은 회사가 제공하는 <strong>나니아 터미널</strong> 및 AI 교육 콘텐츠를 이용함에 있어 필요한 제반 사항을 규정합니다.</p>
            <ol>
                <li>"나니아 터미널"이란 이용자가 웹 브라우저를 통해 AI를 학습시키고 코딩할 수 있도록 제공되는 지능형 플랫폼입니다.</li>
                <li>"이용자"란 회사의 서비스에 접속하여 본 약관에 따라 혜택을 받는 모든 회원을 의미합니다.</li>
            </ol>
        </SectionBox>

        <SectionBox>
            <h3><FiCheckCircle /> 제2조 (서비스의 제공)</h3>
            <p>회사는 이용자에게 다음과 같은 전문 교육 서비스를 제공합니다.</p>
            <ul>
                <li>실전 텍스트 코딩 기반의 프로젝트 학습 환경</li>
                <li>AI 튜터를 활용한 실시간 코드 리뷰 및 멘토링</li>
                <li>개인별 프로젝트 결과물의 웹 호스팅 및 배포 지원</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiAlertCircle /> 제3조 (이용자 의무 및 안전 수칙)</h3>
            <p>나니아랩은 상호 존중하는 교육 환경을 위해 다음과 같은 안전 수칙을 운영합니다.</p>
            <ol>
                <li><strong>클린 채팅 준수</strong>: 비속어, 욕설 등 타인에게 불쾌감을 주는 언어 사용 시 세이프티 가드에 의해 이용이 제한될 수 있습니다.</li>
                <li><strong>시스템 보안 유지</strong>: 서비스의 취약점을 악용하거나 비정상적인 방법으로 서버에 부하를 주는 행위를 금지합니다.</li>
            </ol>
        </SectionBox>

        <SectionBox>
            <h3><FiFileText /> 제4조 (저작권 규정)</h3>
            <p>회원은 자신이 직접 창작한 결과물에 대해 <strong>회사와 공동으로</strong> 권리를 가집니다.</p>
            <ol>
                <li>이용자가 작성한 코드 및 프로젝트의 저작권은 <strong>이용자 본인 및 나니아랩</strong>에 귀속됩니다.</li>
                <li>회사는 우수 사례 홍보, 교육적 연구 및 서비스 개선을 위해 이용자의 창작물을 활용할 수 있습니다.</li>
            </ol>
        </SectionBox>

        <p className="text-center text-slate-500 text-sm mt-4">
            공고 일자: 2025년 12월 3일 | <strong>나니아랩 (NARNIA LAB)</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
            variant="primary" 
            style={{ 
                borderRadius: '13px',
                fontWeight: 760,
                padding: '0.75rem 2.5rem',
                background: 'linear-gradient(135deg, #183f6d, #347bc0)',
                border: 'none',
                boxShadow: '0 12px 26px rgba(35, 91, 149, 0.22)'
            }} 
            onClick={onHide}
        >
            확인 및 닫기
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default TermsModal;
